import React, { useEffect, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { DeleteOutlined, RiseOutlined, FallOutlined } from '@ant-design/icons';
import humanizeUtil from '@src/utils/humanizeUtil';
import tracker from '@src/utils/sensor/apptracker';
import {
  Row,
  Cell,
  FullCell,
  TrendValue,
  CellNumber,
  CellPrimary,
  CellSecondary,
  DeletedEventCell,
  DeletedEventAlert,
  DeleteButtonContainer,
  DeleteButton,
} from './styled';
import { Retry } from '@src/components/common';
import { get } from 'lodash';
import ErrorBoundary from '@src/components/common/Retry/ErrorBoundary';
import {
  appliedFilterAtom,
  appliedIntervalAtom,
  enableDodAtom,
  removeEventAtom,
} from '../../atoms';
import {
  openEventsPreset,
  generateEventsPresetUrl,
  generateEventsPresetUrlWithStorage,
} from '../../drillDown';
import { shouldUseBrowserDefaultNavigation } from '../../utils';
import useEventMetricsData from '../hooks/useEventMetricsData';
import useFetchDodEventMetricsData from '../hooks/useFetchDodEventMetricsData';
import { EVENT_METRICS } from '@src/hooks/event/useEventConfigs';

const RowData = ({ eventMeta, nameColumnWidth, columnWidth, data, onDataUpdate, dodData }) => {
  const appliedFilter = useAtomValue(appliedFilterAtom);
  const appliedInterval = useAtomValue(appliedIntervalAtom);
  const enableDod = useAtomValue(enableDodAtom);
  const navigate = useNavigate();

  const countData = get(data, ['totals', EVENT_METRICS.count], null);
  const withUserData = get(data, ['totals', EVENT_METRICS.with_user], null);
  const withSessionData = get(data, ['totals', EVENT_METRICS.with_session], null);
  const withUserPercentageData = get(data, ['totals', EVENT_METRICS.with_user_percentage], null);
  const withSessionPercentageData = get(
    data,
    ['totals', EVENT_METRICS.with_session_percentage],
    null,
  );

  // Get DoD totals for trend indicators
  const countDodData = enableDod ? get(dodData, ['totals', EVENT_METRICS.count], null) : null;
  const withSessionDodData = enableDod
    ? get(dodData, ['totals', EVENT_METRICS.with_session], null)
    : null;
  const withUserDodData = enableDod
    ? get(dodData, ['totals', EVENT_METRICS.with_user], null)
    : null;

  // Report the impacted sessions value for sorting
  useEffect(() => {
    if (onDataUpdate && withSessionData !== null && withSessionData !== undefined) {
      onDataUpdate((prev) => ({
        ...prev,
        [eventMeta.id]: withSessionData,
      }));
    }
  }, [withSessionData, eventMeta.id, onDataUpdate]);

  const countHumanized = humanizeUtil(countData, EVENT_METRICS.count);
  const withUserHumanized = humanizeUtil(withUserData, EVENT_METRICS.with_user);
  const withSessionHumanized = humanizeUtil(withSessionData, EVENT_METRICS.with_session);
  const withUserHumanizedPercentage = humanizeUtil(
    withUserPercentageData,
    EVENT_METRICS.with_user_percentage,
  );
  const withSessionHumanizedPercentage = humanizeUtil(
    withSessionPercentageData,
    EVENT_METRICS.with_session_percentage,
  );
  // Calculate percentage changes for trend indicators
  const calculatePercentageChange = (current: number | null, previous: number | null) => {
    if (current === null || previous === null || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  const countPercentageChange = useMemo(
    () => calculatePercentageChange(countData, countDodData),
    [countData, countDodData],
  );

  const sessionPercentageChange = useMemo(
    () => calculatePercentageChange(withSessionData, withSessionDodData),
    [withSessionData, withSessionDodData],
  );

  const userPercentageChange = useMemo(
    () => calculatePercentageChange(withUserData, withUserDodData),
    [withUserData, withUserDodData],
  );

  const countPercentageText =
    countPercentageChange !== null && countPercentageChange !== 0
      ? `${countPercentageChange > 0 ? '+' : ''}${countPercentageChange.toFixed(1)}%`
      : countPercentageChange === 0
        ? '0%'
        : '--';

  const isCountIncrease = countPercentageChange !== null && countPercentageChange > 0;
  const isCountNeutral = countPercentageChange === 0 || countPercentageChange === null;

  // For events: more is generally good (green), less is neutral
  // So: increase (positive change) = green with +, decrease (negative change) = red with -
  const sessionPercentageText =
    sessionPercentageChange !== null && sessionPercentageChange !== 0
      ? `${sessionPercentageChange > 0 ? '+' : ''}${sessionPercentageChange.toFixed(1)}%`
      : sessionPercentageChange === 0
        ? '0%'
        : '--';

  const userPercentageText =
    userPercentageChange !== null && userPercentageChange !== 0
      ? `${userPercentageChange > 0 ? '+' : ''}${userPercentageChange.toFixed(1)}%`
      : userPercentageChange === 0
        ? '0%'
        : '--';

  // Color: green if increased (more is good), red if decreased, grey if no change or no value
  const isSessionIncrease = sessionPercentageChange !== null && sessionPercentageChange > 0;
  const isUserIncrease = userPercentageChange !== null && userPercentageChange > 0;
  const isSessionNeutral = sessionPercentageChange === 0 || sessionPercentageChange === null;
  const isUserNeutral = userPercentageChange === 0 || userPercentageChange === null;

  const eventUrl = generateEventsPresetUrl({ eventMeta }) || '#';

  const onEventClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Allow browser default behavior for Ctrl/Cmd+Click, middle-click, etc.
    if (shouldUseBrowserDefaultNavigation(e)) {
      return;
    }

    // For normal clicks, prevent default and navigate in same tab
    e.preventDefault();
    tracker('Click preset link from Overview Table Events', {
      eventName: eventMeta.id,
    });
    openEventsPreset({ eventMeta, appliedFilter, appliedInterval, navigate });
  };

  const onEventContextMenu = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const url = generateEventsPresetUrlWithStorage({
      eventMeta,
      appliedFilter,
      appliedInterval,
    });
    if (url) {
      tracker('Right-click preset link from Overview Table Events', {
        eventName: eventMeta.id,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Row href={eventUrl} onClick={onEventClick} onContextMenu={onEventContextMenu}>
      <FullCell $width={nameColumnWidth}>{eventMeta.name}</FullCell>
      <Cell $width={columnWidth}>
        <CellPrimary>
          {countHumanized.humanizedNumber} {countHumanized.humanizedUnit}
        </CellPrimary>
        {enableDod && (
          <TrendValue $positive={isCountIncrease} $neutral={isCountNeutral}>
            {!isCountNeutral && (countPercentageChange! > 0 ? <RiseOutlined /> : <FallOutlined />)}
            {countPercentageText}
          </TrendValue>
        )}
      </Cell>
      <Cell $width={columnWidth}>
        <CellNumber>
          <CellPrimary>
            {withSessionHumanizedPercentage.humanizedNumber}{' '}
            {withSessionHumanizedPercentage.humanizedUnit}
          </CellPrimary>
          <CellSecondary>
            {withSessionHumanized.humanizedNumber} {withSessionHumanized.humanizedUnit}
          </CellSecondary>
        </CellNumber>
        {enableDod && (
          <TrendValue $positive={isSessionIncrease} $neutral={isSessionNeutral}>
            {!isSessionNeutral &&
              (sessionPercentageChange! > 0 ? <RiseOutlined /> : <FallOutlined />)}
            {sessionPercentageText}
          </TrendValue>
        )}
      </Cell>
      <Cell $width={columnWidth}>
        <CellNumber>
          <CellPrimary>
            {withUserHumanizedPercentage.humanizedNumber}{' '}
            {withUserHumanizedPercentage.humanizedUnit}
          </CellPrimary>
          <CellSecondary>
            {withUserHumanized.humanizedNumber} {withUserHumanized.humanizedUnit}
          </CellSecondary>
        </CellNumber>
        {enableDod && (
          <TrendValue $positive={isUserIncrease} $neutral={isUserNeutral}>
            {!isUserNeutral && (userPercentageChange! > 0 ? <RiseOutlined /> : <FallOutlined />)}
            {userPercentageText}
          </TrendValue>
        )}
      </Cell>
    </Row>
  );
};

const ActiveEventRow = ({
  eventMeta,
  nameColumnWidth,
  columnWidth,
  query,
  onDataUpdate,
  dodQuery,
}) => {
  return (
    <ErrorBoundary>
      <Retry
        progress
        isLoading={query.isFetching}
        showError={query.isError}
        fetchData={query.refetch}
        queued={query.queued}
        horizontal={true}
        compact={true}
      >
        <RowData
          eventMeta={eventMeta}
          nameColumnWidth={nameColumnWidth}
          columnWidth={columnWidth}
          data={query.data}
          onDataUpdate={onDataUpdate}
          dodData={dodQuery?.data}
        />
      </Retry>
    </ErrorBoundary>
  );
};

const DeletedEventRow = ({ eventMeta, nameColumnWidth, columnWidth }) => {
  const removeEvent = useSetAtom(removeEventAtom);

  const handleRemoveEvent = () => {
    removeEvent(String(eventMeta.id));
  };

  return (
    <Row as="div">
      <DeletedEventCell $width={nameColumnWidth}>
        <DeletedEventAlert type="error" message="This event has been deleted" banner />
      </DeletedEventCell>
      <Cell $width={columnWidth} />
      <Cell $width={columnWidth} />
      <Cell $width={columnWidth}>
        <DeleteButtonContainer style={{ width: '100%' }}>
          <DeleteButton
            type="text"
            size="large"
            icon={<DeleteOutlined />}
            onClick={handleRemoveEvent}
            danger
            title="Remove this event from the dashboard"
          />
        </DeleteButtonContainer>
      </Cell>
    </Row>
  );
};

const ActiveEventRowContainer = ({ signalMeta, nameColumnWidth, columnWidth, onDataUpdate }) => {
  const eventName = signalMeta?.id || '';
  const enableDod = useAtomValue(enableDodAtom);

  // Fetch metrics for this specific event (with event_name as filter)
  const query = useEventMetricsData({ eventName, eventMeta: signalMeta });

  // Fetch DoD data when enabled
  const dodQuery = useFetchDodEventMetricsData({
    eventName,
    eventMeta: signalMeta,
    enabled: enableDod,
  });

  return (
    <ActiveEventRow
      eventMeta={signalMeta}
      nameColumnWidth={nameColumnWidth}
      columnWidth={columnWidth}
      query={query}
      onDataUpdate={onDataUpdate}
      dodQuery={enableDod ? dodQuery : null}
    />
  );
};

export const RowComponent = ({ signalMeta, nameColumnWidth, columnWidth, onDataUpdate }) => {
  if (signalMeta?.isDeleted) {
    return (
      <DeletedEventRow
        eventMeta={signalMeta}
        nameColumnWidth={nameColumnWidth}
        columnWidth={columnWidth}
      />
    );
  }

  return (
    <ActiveEventRowContainer
      signalMeta={signalMeta}
      nameColumnWidth={nameColumnWidth}
      columnWidth={columnWidth}
      onDataUpdate={onDataUpdate}
    />
  );
};
